import { BaseModernLayouter } from './BaseModernLayouter';
import { LayoutData, SpacingMode } from '../LayoutData';
import { UpVectorMode, AlignmentMode } from '../../field/FieldTypes';
import { POI } from '../../poi/POI';

// Import our modern layouters (JS files)
declare const ModernLayouters: any;

// ========================================
// 2D/3D SURFACE LAYOUTERS
// ========================================

/**
 * Modern Sphere Grid Layouter - Grid pattern on sphere surface
 */
export class ModernSphereGridLayouter extends BaseModernLayouter {
    constructor() {
        super(
            'modern-sphere-grid', 
            'Modern Sphere Grid Layouter (Objects in grid on sphere surface)',
            {
                modernSphereGridFixedSpacing: 1.5,
                modernFlip180: false
            },
            '2d',
            [SpacingMode.Fixed, SpacingMode.Distribute]
        );
    }

    // ========================================
    // IMPLEMENTATION OF ABSTRACT METHODS
    // ========================================

    protected getModernLayouterFunction(): (config: any) => any[] {
        return ModernLayouters.sphereGrid;
    }

    protected createConfig(pois: POI[], layoutData: LayoutData): any {
        return {
            objectCount: pois.length,
            spacingMode: this.getSpacingModeString(layoutData),
            spacing: this.getParameter(layoutData, 'modernSphereGridFixedSpacing', 1.5),
            pathType: 'sphere_grid',
            orientation: layoutData.alignmentMode?.value === AlignmentMode.Aligned ? 'aligned' : 'perpendicular',
            upMode: layoutData.upVectorMode?.value === UpVectorMode.WorldUp ? 'world' : 'path',
            flip180: this.getFlipParameter(layoutData),
            radius: this.getParameter(layoutData, 'modernCircleRadius', 5.0), // 🔄 Unified radius for all modern layouters
            time: 0
        };
    }

    protected getLayouterName(): string {
        return 'ModernSphereGridLayouter';
    }

    validateParameters(layoutData: LayoutData): boolean {
        const spacing = this.getParameter(layoutData, 'modernSphereGridFixedSpacing', 1.5);
        return spacing > 0;
    }
}

/**
 * Modern Plane Grid Layouter - Rectangular grid on flat plane
 */
export class ModernPlaneGridLayouter extends BaseModernLayouter {
    constructor() {
        super(
            'modern-plane-grid', 
            'Modern Plane Grid Layouter (Rectangular grid on flat plane)',
            {
                modernPlaneGridFixedSpacing: 2.0,
                modernFlip180: false
            },
            '2d',
            [SpacingMode.Fixed, SpacingMode.Distribute]
        );
    }

    protected getModernLayouterFunction(): (config: any) => any[] {
        return ModernLayouters.planeGrid;
    }

    protected createConfig(pois: POI[], layoutData: LayoutData): any {
        return {
            objectCount: pois.length,
            spacingMode: this.getSpacingModeString(layoutData),
            spacing: this.getParameter(layoutData, 'modernPlaneGridFixedSpacing', 2.0),
            pathType: 'plane_grid',
            orientation: layoutData.alignmentMode?.value === AlignmentMode.Aligned ? 'aligned' : 'perpendicular',
            upMode: layoutData.upVectorMode?.value === UpVectorMode.WorldUp ? 'world' : 'path',
            flip180: this.getFlipParameter(layoutData),
            time: 0
        };
    }

    protected getLayouterName(): string {
        return 'ModernPlaneGridLayouter';
    }

    validateParameters(layoutData: LayoutData): boolean {
        const spacing = this.getParameter(layoutData, 'modernPlaneGridFixedSpacing', 2.0);
        return spacing > 0;
    }
}

/**
 * Modern Sphere Hex Layouter - Hexagonal pattern on sphere (Fibonacci distribution)
 */
export class ModernSphereHexLayouter extends BaseModernLayouter {
    constructor() {
        super(
            'modern-sphere-hex', 
            'Modern Sphere Hex Layouter (Hexagonal pattern on sphere surface)',
            {
                modernSphereHexFixedSpacing: 1.0,
                modernFlip180: false
            },
            '2d',
            [SpacingMode.Fixed, SpacingMode.Distribute]
        );
    }

    protected getModernLayouterFunction(): (config: any) => any[] {
        return ModernLayouters.sphereHex;
    }

    protected createConfig(pois: POI[], layoutData: LayoutData): any {
        return {
            objectCount: pois.length,
            spacingMode: this.getSpacingModeString(layoutData),
            spacing: this.getParameter(layoutData, 'modernSphereHexFixedSpacing', 1.0),
            pathType: 'sphere_hex',
            orientation: layoutData.alignmentMode?.value === AlignmentMode.Aligned ? 'aligned' : 'perpendicular',
            upMode: layoutData.upVectorMode?.value === UpVectorMode.WorldUp ? 'world' : 'path',
            flip180: this.getFlipParameter(layoutData),
            radius: this.getParameter(layoutData, 'modernCircleRadius', 5.0), // 🔄 Unified radius for all modern layouters
            time: 0
        };
    }

    protected getLayouterName(): string {
        return 'ModernSphereHexLayouter';
    }

    validateParameters(layoutData: LayoutData): boolean {
        const spacing = this.getParameter(layoutData, 'modernSphereHexFixedSpacing', 1.0);
        return spacing > 0;
    }
}

/**
 * Modern Sphere Hex Rings Layouter - Hexagonal rings from center outward on sphere
 */
export class ModernSphereHexRingsLayouter extends BaseModernLayouter {
    constructor() {
        super(
            'modern-sphere-hex-rings', 
            'Modern Sphere Hex Rings Layouter (Hexagonal rings from center outward)',
            {
                modernSphereHexRingsFixedSpacing: 1.2,
                modernFlip180: false
            },
            '2d',
            [SpacingMode.Fixed, SpacingMode.Distribute]
        );
    }

    protected getModernLayouterFunction(): (config: any) => any[] {
        return ModernLayouters.sphereHexRings;
    }

    protected createConfig(pois: POI[], layoutData: LayoutData): any {
        return {
            objectCount: pois.length,
            spacingMode: this.getSpacingModeString(layoutData),
            spacing: this.getParameter(layoutData, 'modernSphereHexRingsFixedSpacing', 1.2),
            pathType: 'sphere_hex', // Use same orientation logic
            orientation: layoutData.alignmentMode?.value === AlignmentMode.Aligned ? 'aligned' : 'perpendicular',
            upMode: layoutData.upVectorMode?.value === UpVectorMode.WorldUp ? 'world' : 'path',
            flip180: this.getFlipParameter(layoutData),
            radius: this.getParameter(layoutData, 'modernCircleRadius', 5.0), // 🔄 Unified radius for all modern layouters
            time: 0
        };
    }

    protected getLayouterName(): string {
        return 'ModernSphereHexRingsLayouter';
    }

    validateParameters(layoutData: LayoutData): boolean {
        const spacing = this.getParameter(layoutData, 'modernSphereHexRingsFixedSpacing', 1.2);
        return spacing > 0;
    }
}

/**
 * Modern Plane Hex Rings Layouter - Hexagonal rings on flat plane
 */
export class ModernPlaneHexRingsLayouter extends BaseModernLayouter {
    constructor() {
        super(
            'modern-plane-hex-rings', 
            'Modern Plane Hex Rings Layouter (Hexagonal rings on flat plane)',
            {
                modernPlaneHexRingsFixedSpacing: 1.8,
                modernFlip180: false
            },
            '2d',
            [SpacingMode.Fixed, SpacingMode.Distribute]
        );
    }

    protected getModernLayouterFunction(): (config: any) => any[] {
        return ModernLayouters.planeHexRings;
    }

    protected createConfig(pois: POI[], layoutData: LayoutData): any {
        return {
            objectCount: pois.length,
            spacingMode: this.getSpacingModeString(layoutData),
            spacing: this.getParameter(layoutData, 'modernPlaneHexRingsFixedSpacing', 1.8),
            pathType: 'plane_hex', // Plane hexagonal orientation
            orientation: layoutData.alignmentMode?.value === AlignmentMode.Aligned ? 'aligned' : 'perpendicular',
            upMode: layoutData.upVectorMode?.value === UpVectorMode.WorldUp ? 'world' : 'path',
            flip180: this.getFlipParameter(layoutData),
            time: 0
        };
    }

    protected getLayouterName(): string {
        return 'ModernPlaneHexRingsLayouter';
    }

    validateParameters(layoutData: LayoutData): boolean {
        const spacing = this.getParameter(layoutData, 'modernPlaneHexRingsFixedSpacing', 1.8);
        return spacing > 0;
    }
}

/**
 * Modern Sphere Hex Honeycomb Layouter - True seamless honeycomb tessellation on sphere
 */
export class ModernSphereHexHoneycombLayouter extends BaseModernLayouter {
    constructor() {
        super(
            'modern-sphere-hex-honeycomb', 
            'Modern Sphere Hex Honeycomb Layouter (Seamless honeycomb tessellation)',
            {
                modernSphereHexHoneycombFixedSpacing: 1.0,
                modernFlip180: false
            },
            '2d',
            [SpacingMode.Fixed, SpacingMode.Distribute]
        );
    }

    protected getModernLayouterFunction(): (config: any) => any[] {
        return ModernLayouters.sphereHexHoneycomb;
    }

    protected createConfig(pois: POI[], layoutData: LayoutData): any {
        return {
            objectCount: pois.length,
            spacingMode: this.getSpacingModeString(layoutData),
            spacing: this.getParameter(layoutData, 'modernSphereHexHoneycombFixedSpacing', 1.0),
            pathType: 'sphere_hex', // Use same orientation logic
            orientation: layoutData.alignmentMode?.value === AlignmentMode.Aligned ? 'aligned' : 'perpendicular',
            upMode: layoutData.upVectorMode?.value === UpVectorMode.WorldUp ? 'world' : 'path',
            flip180: this.getFlipParameter(layoutData),
            radius: this.getParameter(layoutData, 'modernCircleRadius', 5.0), // 🔄 Unified radius for all modern layouters
            cellFill: this.getParameter(layoutData, 'honeycombCellFill', 0.93),
            time: 0
        };
    }

    protected getLayouterName(): string {
        return 'ModernSphereHexHoneycombLayouter';
    }

    validateParameters(layoutData: LayoutData): boolean {
        const spacing = this.getParameter(layoutData, 'modernSphereHexHoneycombFixedSpacing', 1.0);
        return spacing > 0;
    }
}